import './Onboarding.css';

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getOnboardingStatus, setOnboardingStatus } from '@vse/api/machinemotion';
import { Pages } from '@src/Pages';
import { Spinner, Redirect } from '@src/components';
import { Disclaimer } from './Disclaimer';
import { WifiOnboarding } from './wifi_onboarding';
import { MachineCloudOnboarding } from './MachineCloudOnboarding';
import { SystemPasswordOnboarding } from './system_password_onboarding';
import { useConfigInfo } from '@src/contexts';
import { type Location } from 'react-router-dom';

enum OnboardingStep {
  DISCLAIMER = 1,
  SYSTEM_PASSWORD,
  WIFI,
  MACHINE_CLOUD_SYNC,
  DONE,
}

interface OnboardingState {
  disclaimerRead: boolean
  passwordSet: boolean
  onboarded: boolean
}

const SHOW_PASSWORD_VIEW = 'showPasswordView';

function getAvailableOnboardingSteps (isPendantAvailable: boolean, onboardingState: OnboardingState, location: Location): OnboardingStep[] {
  // cases when we want to execute only a specific flow
  const params = new URLSearchParams(location.search);
  if (params.has(SHOW_PASSWORD_VIEW)) {
    return [OnboardingStep.SYSTEM_PASSWORD, OnboardingStep.DONE];
  }

  // regular flow
  const disclaimerStep = !onboardingState.disclaimerRead ? [OnboardingStep.DISCLAIMER] : [];
  const passwordStep = !onboardingState.passwordSet ? [OnboardingStep.SYSTEM_PASSWORD] : [];
  const wifiSteps = isPendantAvailable && !onboardingState.onboarded ? [OnboardingStep.WIFI, OnboardingStep.MACHINE_CLOUD_SYNC] : [];

  return [...disclaimerStep, ...passwordStep, ...wifiSteps, OnboardingStep.DONE];
}

function getNextStep (currentOnboardingStep: OnboardingStep, onboardingSteps: OnboardingStep[]): OnboardingStep {
  const currentStepIndex = onboardingSteps.findIndex(x => x === currentOnboardingStep);
  if (currentStepIndex < 0) { return OnboardingStep.DONE; }

  const nextStep = currentStepIndex + 1;
  if (nextStep >= onboardingSteps.length - 1) { return OnboardingStep.DONE; }

  return onboardingSteps[nextStep];
}

interface OnboardingProps {
  isPendantAvailable: boolean
}

export const Onboarding = ({ isPendantAvailable }: OnboardingProps): JSX.Element => {
  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStep[] | undefined>(undefined);
  const location = useLocation();
  const { configInfoFetched } = useConfigInfo();

  useEffect(() => {
    void getOnboardingStatus((onboardingState: OnboardingState) => {
      const availableOnboardingSteps = getAvailableOnboardingSteps(isPendantAvailable, onboardingState, location);
      setOnboardingSteps(availableOnboardingSteps);
    });
  }, []);

  if ((onboardingSteps === undefined) || !configInfoFetched) {
    return <Spinner />;
  }

  return (
    <div id="onboarding">
      <OnboardingStepComponent
        onboardingSteps={onboardingSteps}
        isPendantAvailable={isPendantAvailable}
      />
    </div>
  );
};

interface OnboardingStepComponentProps {
  onboardingSteps: OnboardingStep[]
  isPendantAvailable: boolean
}

function OnboardingStepComponent ({ onboardingSteps, isPendantAvailable }: OnboardingStepComponentProps): JSX.Element {
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(onboardingSteps[0]);
  const configInfoContext = useConfigInfo();

  const nextStep = getNextStep(onboardingStep, onboardingSteps);

  switch (onboardingStep) {
    case OnboardingStep.DISCLAIMER:
      return <Disclaimer onComplete={() => {
        void setOnboardingStatus({ disclaimerRead: true }, () => { setOnboardingStep(nextStep); });
      }} />;

    case OnboardingStep.SYSTEM_PASSWORD:
      return <SystemPasswordOnboarding
        onComplete={() => {
          // when no pendant is connected, we will set as complete the onboarding (wifi/cloud)
          void setOnboardingStatus({ passwordSet: true, onboarded: isPendantAvailable ? undefined : true }, () => {
            setOnboardingStep(nextStep);
            configInfoContext.fetchConfig();
          });
        }}
      />;

    case OnboardingStep.WIFI:
      return <WifiOnboarding onComplete={() => {
        setOnboardingStep(nextStep);
      }} />;

    case OnboardingStep.MACHINE_CLOUD_SYNC:
      return <MachineCloudOnboarding
        onComplete={() => { void setOnboardingStatus({ onboarded: true }, () => { setOnboardingStep(nextStep); }); }}
        onCancel={() => { void setOnboardingStatus({ onboarded: true }, () => { setOnboardingStep(OnboardingStep.WIFI); }); }}
      />;

    case OnboardingStep.DONE:
    default:
      return <Redirect pathname={Pages.MENU}/>;
  }
}
